import axios from "axios";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// `withCredentials: true` so the httpOnly refresh cookie flows on every
// request. Without this the /auth/refresh endpoint can't read the cookie
// and silent-refresh fails.
const client = axios.create({ baseURL: API, withCredentials: true });

client.interceptors.request.use((config) => {
  // Coach-share mode: when browsing under /share/progress/:token (either the
  // main mirror or the /photos sub-page), send the per-token X-Share-Access
  // JWT instead of the owner's bearer token. This lets us reuse every GET
  // endpoint that Progress/ProgressPhotos already call (summary, weight,
  // photos, goal-history, auth/me) with zero endpoint duplication.
  const path = typeof window !== "undefined" ? window.location.pathname : "";
  const shareMatch = path.match(/^\/share\/progress\/([^/]+)/);
  if (shareMatch) {
    const shareAccess = sessionStorage.getItem(`share-${shareMatch[1]}`);
    if (shareAccess) {
      config.headers["X-Share-Access"] = shareAccess;
      // Do NOT attach the Authorization header — this request is read-only.
      return config;
    }
  }
  const token = localStorage.getItem("calofay_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  const variant = localStorage.getItem("calofay_variant");
  if (variant) config.headers["X-Variant"] = variant;
  return config;
});

// ── Silent token refresh ──────────────────────────────────────────────
// When the 60-min access token expires the server returns 401. We call
// /auth/refresh once (using the httpOnly cookie), swap the new access
// token into localStorage, and retry the ORIGINAL failing request. The
// user never sees the 401.
//
// `refreshPromise` deduplicates concurrent 401s: if 4 tabs all hit 401
// at once, we only call /auth/refresh once and every caller awaits the
// same promise.
// Persist access + device refresh token (iOS WKWebView can drop the cookie).
export function saveSession(data) {
  if (data?.token) { localStorage.setItem("calofay_token", data.token); localStorage.removeItem("calofay_signed_out"); }
  if (data?.refresh_token) localStorage.setItem("calofay_refresh", data.refresh_token);
}

let refreshPromise = null;
// True only when the server explicitly rejected the refresh (401/403) — a slow or
// overloaded server (timeout / 5xx / network) must NOT sign the user out.
let _lastRefreshHard = false;
export const lastRefreshWasRejected = () => _lastRefreshHard;
export async function silentRefresh() {
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    try {
      const stored = localStorage.getItem("calofay_refresh");
      const resp = await axios.post(`${API}/auth/refresh`, stored ? { refresh_token: stored } : {}, { withCredentials: true });
      const newToken = resp.data?.token;
      _lastRefreshHard = !newToken;
      if (newToken) {
        saveSession(resp.data);
        return newToken;
      }
      return null;
    } catch (e) {
      _lastRefreshHard = [400, 401, 403].includes(e?.response?.status);
      return null;
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
}

// ── Proactive background refresh ──────────────────────────────────────
// Access tokens live 60 min. Waiting for a request to 401 means the
// user's first click after ~60 min shows a spinner while we refresh.
// Instead, run a lightweight refresh every 45 min so the token is
// always fresh AND the refresh cookie's sliding window keeps rolling
// (server extends the cookie on every successful /auth/refresh call).
// The interval starts on module load and pauses when the tab is
// hidden to avoid pointless mobile background wakeups. `visibilitychange`
// fires a top-up refresh the moment the user returns to the tab.
const PROACTIVE_REFRESH_MS = 45 * 60 * 1000;
let _refreshInterval = null;
function scheduleProactiveRefresh() {
  if (typeof window === "undefined") return;
  if (_refreshInterval) return;
  const tick = () => {
    if (document.visibilityState === "hidden") return;
    // Only refresh if a token exists — no point if user is logged out.
    if (!localStorage.getItem("calofay_token") && !localStorage.getItem("calofay_refresh")) return;
    silentRefresh();
  };
  _refreshInterval = setInterval(tick, PROACTIVE_REFRESH_MS);
  // Top-up refresh when returning to the tab so a long-backgrounded
  // session catches up before the user's first tap.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible" && localStorage.getItem("calofay_token")) {
      silentRefresh();
    }
  });
}
scheduleProactiveRefresh();

client.interceptors.response.use(
  (r) => {
    // First successful response after an outage → let the offline banner know
    // it can dismiss itself without waiting for the OS `online` event.
    if (typeof window !== "undefined") {
      window.dispatchEvent(new Event("calofay:network-ok"));
    }
    return r;
  },
  async (err) => {
    const path = typeof window !== "undefined" ? window.location.pathname : "";
    const isShare = /^\/share\/progress\//.test(path);
    const orig = err.config || {};
    const status = err.response?.status;

    // 401 → attempt one silent refresh, then retry the original request.
    // Skip on: share view, the /auth/refresh call itself, or if we already retried.
    // ALSO skip on the auth endpoints themselves — a 401 from
    // /auth/login or /auth/signup means "wrong credentials", NOT
    // "session expired". Without this guard the response interceptor
    // would rewrite the backend's helpful "بيانات دخول غير صحيحة"
    // message with "انتهت صلاحية جلستك" and confuse the user.
    const isRefreshCall = (orig.url || "").includes("/auth/refresh");
    const isAuthEntryCall = /\/auth\/(login|signup)(?:$|\?)/.test(orig.url || "");
    if (status === 401 && !isShare && !isRefreshCall && !isAuthEntryCall && !orig._retried) {
      orig._retried = true;
      const newToken = await silentRefresh();
      if (newToken) {
        orig.headers = { ...(orig.headers || {}), Authorization: `Bearer ${newToken}` };
        return client(orig);
      }
      // Server busy / unreachable → keep the session, just fail this request.
      if (!_lastRefreshHard) return Promise.reject(err);
      // Refresh failed → user really is signed out. Drop the token so
      // the guarded routes bounce to /login, then swap the raw
      // FastAPI "Not authenticated" detail with a friendly localized
      // message so any downstream `toast.error(err.response.data.detail)`
      // shows something helpful. Also emit an app-wide event that any
      // page can listen for to redirect / clear state / show a modal.
      //
      // IMPORTANT: only run the session-expired flow if a token
      // ACTUALLY existed before this 401. Fresh installs (iOS
      // onboarding, marketing landing) hit /auth/me with no token —
      // that's expected, not a session expiry, so don't hijack them
      // to /login before they've even signed up.
      const hadToken = !!localStorage.getItem("calofay_token");
      localStorage.removeItem("calofay_token");
      localStorage.removeItem("calofay_refresh");
      localStorage.removeItem("calofay_user_cache");
      const lang = (typeof localStorage !== "undefined" && localStorage.getItem("calofay:lang")) || "ar";
      const friendly = lang === "en"
        ? "Your session expired — please sign in again."
        : "انتهت صلاحية جلستك — سجّل دخولك مرة أخرى.";
      if (err.response && err.response.data && typeof err.response.data === "object") {
        err.response.data.detail = friendly;
      }
      if (hadToken && typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("calofay:session-expired"));
        // Bounce to /login after a short delay so any active toast has
        // time to render. Guarded so we don't loop back to /login when
        // the user is already on the auth flow.
        const p = window.location.pathname;
        const publicPaths = ["/login", "/signup", "/welcome", "/quiz", "/onboarding", "/share", "/ios/", "/reset", "/activate", "/terms", "/privacy"];
        const onPublic = publicPaths.some((pref) => p.startsWith(pref)) || p === "/";
        if (!onPublic) {
          setTimeout(() => { window.location.href = "/login"; }, 800);
        }
      }
    }

    // True network failure (no response body means we never reached the
    // server — DNS/wifi/captive-portal/timeout). Surface it to the offline
    // banner so the user gets a clear "لا يوجد اتصال" indicator instead of
    // an ambiguous silent toast.
    if (!err.response && typeof window !== "undefined") {
      window.dispatchEvent(new Event("calofay:network-error"));
    }
    return Promise.reject(err);
  }
);

export default client;
// Named exports for pages/components that expect `import { api } from "@/lib/api"`
// (the recipes bundle uses this pattern).
export { client as api };

/**
 * Best-effort extractor for FastAPI-style error responses.
 * Falls back to the raw `err.message` when we can't recognise the shape.
 */
export function extractError(err) {
  const detail = err?.response?.data?.detail;
  if (Array.isArray(detail) && detail.length && detail[0]?.msg) return detail[0].msg;
  if (typeof detail === "string") return detail;
  if (err?.response?.data?.message) return err.response.data.message;
  return err?.message || "خطأ غير متوقع";
}
